import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { HashRouter as Router, Routes, Route, Link, NavLink, useLocation, useNavigate } from "react-router-dom";

/**
 * Ponno Drobbo Online Ltd – dark luxury static site (single file)
 * ----------------------------------------------------------------
 */

// ==== SETTINGS ====
const COMPANY_NAME = "Ponno Drobbo";
const COMPANY_EMAIL = "sales@ponnodrobboonline.com";
const COMPANY_PHONE = "+44 7518 950393";
const COMPANY_WHATSAPP = COMPANY_PHONE;
const COMPANY_ADDRESS = "1 Bovill Street, Radford, Nottingham, Nottinghamshire, UK, NG73PG";

// Gold accent helpers (Tailwind default palette)
const accentBg = "bg-amber-500";
const accentBgHover = "hover:bg-amber-500/90";
const accentText = "text-amber-300";
const subtleRing = "ring-1 ring-white/10";

// ==== SAMPLE DATA ====
const PRODUCTS = [
  {
    id: "pd-001",
    code: "PDOL-B-0001",
    title: "Chanel 2.55 Handbag in Metallic Blue Python",
    brand: "Chanel",
    category: "Bags",
    condition: "Excellent (9/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 7500,
    description:
      "Chanel 2.55 handbag in metallic blue python with blackened silver hardware and convertible chain strap. Double-flap black leather interior with multiple pockets. Very good condition with light patina and minor signs of wear.",
    images: [
      "/products/PDOL-B-0001.jpg",
    ],
    specs: {
      size: "28 x 19.5 x 7.5 cm",
      material: "Python leather",
      inclusions: "Dust bag, Authenticity card",
      year: "2011",
      Color: "Blue",
    },
  },
  {
    id: "pd-002",
    code: "PDOL-B-0002",
    title: "Hermès Kelly 28cm Handbag - Black Box Leather",
    brand: "Hermès",
    category: "Bags",
    condition: "Good (8/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 9900,
    description:
      "Hermès Kelly 28 in black box leather with gold-plated hardware, top handle, and removable shoulder strap. Features flap closure, black leather lining, zip and patch pockets. Includes tirette, keys, padlock, clochette, and dust bag. Made in France, 1998 (Square B). Good condition with light patina and minor wear.",
    images: [
      "/products/PDOL-B-0002.jpg",
    ],
    specs: {
      size: "28 x 21 x 12 cm",
      material: "Black box leather",
      inclusions: "Dust bag, Authenticity card",
      year: "1998",
      color: "Black",
    },
  },
  {
    id: "pd-004",
    code: "PDOL-W-0001",
    title: "Grand Seiko Evolution 9 'Genbi Valley' Hi-Beat 36000",
    brand: "Grand Seiko",
    category: "Watches",
    condition: "Excellent (9/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 9000,
    description:
      "Grand Seiko Evolution 9 inspired by Japan’s Genbi Valley, featuring a vivid green patterned dial in an Ever-Brilliant steel case. Powered by the Hi-Beat 9SA5 automatic movement with 80-hour power reserve. Limited Edition 122/1000. Sleek 11.7mm profile with refined light-and-shadow polishing for exceptional legibility and comfort.",
    images: [
      "/products/PDOL-W-0001.jpg"
    ],
    specs: {
      model: "SLGH021",
      size: "40 mm",
      material: "Ever-Brilliant Steel",
      dial: "Green patterned, blue seconds hand, date at 3 o'clock",
      movement: "Automatic, Calibre 9SA5, +5/-3 sec/day, 80-hour power reserve",
      waterResistance: "10 bar",
      features: "Limited Edition 122/1000",
      year: "2024"
    }
  },
  {
    id: "pd-003",
    code: "PDOL-B-0003",
    title: "Hermès Kelly 32cm Handbag - Canvas & Leather",
    brand: "Hermès",
    category: "Bags",
    condition: "Excellent (9.5/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 10000,
    description:
      "Hermès Kelly 28 in black box leather with gold-plated hardware, top handle, and removable shoulder strap. Features flap closure, black leather lining with zip and patch pockets. Includes tirette, keys, padlock, clochette, and dust bag. Made in France, 1998 (Square B). Good condition with light patina and minor wear on corners.",
    images: [
      "/products/PDOL-B-0003.jpg",
    ],
    specs: {
      size: "32 x 21 x 12 cm",
      material: "Canvas and white swift leather",
      inclusions: "Dust bag, Authenticity card",
      year: "2007",
      color: "White",
    },
  },
  {
    id: "pd-005",
    code: "PDOL-W-0002",
    title: "OMEGA Speedmaster Chronoscope Co-Axial Master Chronometer 43mm",
    brand: "OMEGA",
    category: "Watches",
    condition: "Excellent (9/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 9850,
    description:
      "OMEGA Speedmaster Chronoscope crafted in exclusive Bronze Gold with an oxidised bronze dial and opaline finish. Features vintage-inspired snail scales for tachymeter, pulsometer, and telemeter functions. Manual-winding Co-Axial Master Chronometer Calibre 9908 visible through the case back. Presented on a brown leather strap with brown ceramic bezel and vintage enamel tachymeter scale.",
    images: [
      "/products/PDOL-W-0002.jpg"
    ],
    specs: {
      model: "329.92.43.51.10.001",
      size: "43 mm",
      material: "Bronze Gold",
      dial: "Oxidised bronze with opaline finish, 18K Bronze Gold PVD hands and numerals",
      bezel: "Polished brown ceramic with vintage enamel tachymeter",
      movement: "Manual-winding OMEGA Co-Axial Master Chronometer Calibre 9908",
      functions: "Chronograph, Tachymeter, Pulsometer, Telemeter",
      band: "Brown leather strap",
      caseBack: "Transparent sapphire crystal",
      waterResistance: "50 m",
      features: "Snail dial design inspired by 1940s chronographs",
      year: "2021"
    }
  },
  {
    id: "pd-006",
    code: "PDOL-W-0003",
    title: "Grand Seiko Evolution 9 Spring Drive ‘Lake Suwa Before Dawn’",
    brand: "Grand Seiko",
    category: "Watches",
    condition: "Excellent (9.5/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 9000,
    description:
      "Grand Seiko Evolution 9 'Lake Suwa Before Dawn' featuring a deep blue wave-textured dial inspired by the tranquil surface of Lake Suwa. Powered by the Spring Drive Calibre 9RA2 with a 5-day power reserve, combining quartz accuracy with mechanical craftsmanship. Stainless steel case and bracelet with screw-down crown, date at 3 o’clock, and power reserve indicator.",
    images: [
      "/products/PDOL-W-0003.jpg"
    ],
    specs: {
      model: "SLGA021",
      size: "40 mm",
      material: "Stainless Steel",
      dial: "Dark blue wave-patterned dial with date at 3 o'clock",
      movement: "Spring Drive, Calibre 9RA2, 5-day power reserve",
      caseBack: "Exhibition sapphire crystal",
      waterResistance: "10 bar",
      band: "Stainless steel bracelet with three-fold clasp",
      features: "Power reserve indicator",
      year: "2023"
    }
  },
  {
    id: "pd-007",
    code: "PDOL-B-0004",
    title: "Louis Vuitton Capucines BB Handbag – Green Ostrich Leather",
    brand: "Louis Vuitton",
    category: "Bags",
    condition: "Excellent (9/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 7800,
    description:
      "Louis Vuitton Capucines BB handbag crafted in exquisite green ostrich leather with silver-tone hardware. Features a structured silhouette with a top handle and an adjustable, removable ostrich leather strap for versatile wear — by hand, on the shoulder, or across the body. Includes a flap closure, green leather-lined interior with two compartments and a zip pocket. Very good condition with light patina on the leather base and minor signs of use.",
    images: [
      "/products/PDOL-B-0004.jpg"
    ],
    specs: {
      model: "Capucines BB",
      size: "26.5 x 17 x 9 cm",
      material: "Green ostrich leather",
      color: "Green",
      inclusions: "Dust bag, Louis Vuitton box",
      signature: "Louis Vuitton Paris Made in France",
      year: "2020",
    }
  },
  {
    id: "pd-008",
    code: "PDOL-B-0005",
    title: "Dior Lady Dior handbag in Blue Denim",
    brand: "Christian Dior",
    category: "Bags",
    condition: "Like New (9.8/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 5000,
    description:
      "Elegant Dior Lady Dior handbag crafted in blue denim with gilt metal hardware. Features the iconic quilted design, double handles, and a removable denim strap allowing the bag to be worn by hand, on the shoulder, or across the body. Includes a flap closure and a blue denim-lined interior with one zip pocket. Signature 'Christian Dior Paris Made in Italy' stamp.",
    images: [
      "/products/PDOL-B-0005.jpg"
    ],
    specs: {
      model: "Lady Dior",
      size: "24 x 20 x 11 cm",
      material: "Blue denim canvas",
      color: "Blue",
      inclusions: "Dust bag, Dior box",
      year: "2020",
    }
  },
  {
    id: "pd-009",
    code: "PDOL-W-0004",
    title: "IWC Big Pilot’s Watch 43 – Blue Dial, Blue Calfskin Strap",
    brand: "IWC Schaffhausen",
    category: "Watches",
    condition: "Great (8.5/10)",
    authenticity: "Authentic – Verified",
    priceGBP: 6500,
    description:
      "IWC Big Pilot’s Watch 43 featuring a rich blue dial with luminescent hands and markers. Housed in a stainless steel case with a screw-in crown and see-through sapphire back. Powered by the IWC-manufactured calibre 82100 with Pellaton automatic winding and a 60-hour power reserve. Delivered on a blue calfskin strap with the IWC EasX-CHANGE® quick-release system.",
    images: [
      "/products/PDOL-W-0004.jpg"
    ],
    specs: {
      model: "IW329303",
      size: "43.0 mm",
      height: "13.6 mm",
      material: "Stainless Steel",
      dial: "Blue with luminescence",
      movement: "IWC calibre 82100, automatic, 60-hour power reserve, 4 Hz (28,800 vph)",
      caseBack: "See-through sapphire glass",
      waterResistance: "10 bar",
      band: "Blue calfskin strap with EasX-CHANGE® quick-release",
      strapWidth: "21.0 mm",
      year: "2022"
    }
  },


  // {
  //   id: "pd-003",
  //   title: "Moncler Maya Down Jacket – Black",
  //   brand: "Moncler",
  //   category: "Jackets",
  //   condition: "Great (8/10)",
  //   authenticity: "Authentic – Verified",
  //   priceGBP: 145000,
  //   description:
  //     "Iconic glossy puffer with detachable hood. Warm and lightweight with signature badge on sleeve.",
  //   images: [
  //     "https://images.unsplash.com/photo-1495121605193-b116b5b9c5fe?q=80&w=1200&auto=format&fit=crop",
  //   ],
  //   specs: {
  //     size: "M (fits 38–40 chest)",
  //     material: "Nylon, down fill",
  //     inclusions: "No original tags",
  //     code: "BVA-MAYA-BLK",
  //   },
  // },
];

// ==== UTILS ====
function classNames(...arr) { return arr.filter(Boolean).join(" "); }
const currencyGBP = (n) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);
const categories = ["All", "Bags", "Watches"];

// ==== COMPONENTS ====
function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {/* Header bar */}
      <header className="sticky top-0 z-40 border-b border-white/10 bg-neutral-950/70 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-3 shrink-0">
              <Link to="/" className="flex items-center gap-2 hover:opacity-90">
                <img src="/logo.png" alt={COMPANY_NAME} className="h-16 w-auto rounded-md md:h-14" />
              </Link>
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6 text-sm text-neutral-300 shrink-0">
              <NavItem to="/catalog">Catalog</NavItem>
              <NavItem to="/about">About</NavItem>
              <NavItem to="/contact">Contact</NavItem>
            </nav>

            {/* Mobile hamburger */}
            <button
              className="md:hidden inline-flex items-center justify-center rounded-xl border border-white/10 bg-neutral-900/60 px-3 py-2 text-white hover:bg-white/10"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50" />

          <aside
            onClick={(e) => e.stopPropagation()}
            className={[
              "absolute right-0 top-0 h-full w-80 max-w-[85%]",
              "bg-neutral-950 border-l border-white/10 shadow-2xl",
              "transform transition-transform duration-200",
              "overflow-y-auto",
              "scrollbar-thin scrollbar-thumb-neutral-700 scrollbar-track-transparent",
            ].join(" ")}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between px-4 h-16 border-b border-white/10 sticky top-0 bg-neutral-950 z-10">
              <span className="text-sm text-neutral-300">Menu</span>
              <button
                className="rounded-xl border border-white/10 bg-neutral-900/60 px-3 py-2 hover:bg-white/10"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                ✕
              </button>
            </div>

            <nav className="p-4 flex flex-col gap-2 pb-20">
              <MobileItem to="/catalog" onClick={() => setOpen(false)}>Catalog</MobileItem>
              <MobileItem to="/about" onClick={() => setOpen(false)}>About</MobileItem>
              <MobileItem to="/contact" onClick={() => setOpen(false)}>Contact</MobileItem>

              {/* <div className="mt-4 pt-4 border-t border-white/10">
                <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Shop</div>
                <MobileItem to="/catalog?category=Bags" onClick={() => setOpen(false)}>Bags</MobileItem>
                <MobileItem to="/catalog?category=Watches" onClick={() => setOpen(false)}>Watches</MobileItem>
              </div> */}
            </nav>
          </aside>
        </div>
      )}
    </>
  );
}

function NavItem({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `hover:text-white transition-colors ${isActive ? "text-amber-400 font-medium" : "text-neutral-300"}`
      }
    >
      {children}
    </NavLink>
  );
}

function MobileItem({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        [
          "block rounded-xl px-3 py-2 border transition-colors",
          isActive
            ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
            : "border-white/10 text-neutral-200 hover:bg-white/5",
        ].join(" ")
      }
    >
      {children}
    </NavLink>
  );
}

function Hero({ onShop }) {
  return (
    <section className="relative">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(251,191,36,0.15),transparent_70%)]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 py-16 md:grid-cols-2 md:items-center">

          {/* Left side: text + mobile image */}
          <div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight text-white">
              Timeless Luxury, Authenticated
            </h1>
            <p className="mt-5 text-neutral-300 leading-relaxed">
              Pre-owned designer bags and watches - carefully sourced, verified, and quality-checked.
              Luxury made accessible, sustainable, and smart.
            </p>

            {/* Mobile image (only visible below paragraph on small screens) */}
            <div className="mt-8 md:hidden">
              <div className={classNames(
                "aspect-[4/3] w-full overflow-hidden rounded-3xl bg-neutral-900 shadow-2xl",
                subtleRing
              )}>
                <img
                  className="h-full w-full object-cover opacity-95"
                  src="/bags_watches.jpg"
                  alt={COMPANY_NAME}
                />
              </div>
            </div>

            {/* Buttons */}
            <div className="mt-8 flex gap-3">
              <button
                onClick={onShop}
                className={classNames(
                  "rounded-2xl px-5 py-3 text-sm font-medium text-neutral-900 shadow",
                  accentBg,
                  accentBgHover
                )}
              >
                Browse Catalog
              </button>
              <a
                href={`mailto:${COMPANY_EMAIL}`}
                className="rounded-2xl px-5 py-3 text-sm font-medium text-white border border-white/10 hover:bg-white/5"
              >
                Email Us
              </a>
            </div>

            {/* Footer badges */}
            <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-neutral-400">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span>Authenticity Verified</span>
              </div>
              <div>Secure, Appointment-Based Purchase</div>
              <div>United Kingdom & International Clients</div>
            </div>
          </div>

          {/* Desktop image (hidden on mobile) */}
          <div className="relative hidden md:block">
            <div className={classNames(
              "aspect-[4/3] w-full overflow-hidden rounded-3xl bg-neutral-900 shadow-2xl",
              subtleRing
            )}>
              <img
                className="h-full w-full object-cover opacity-95"
                src="/bags_watches.jpg"
                alt={COMPANY_NAME}
              />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

function Filters({ activeCategory, setActiveCategory, query, setQuery }) {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setActiveCategory(c)}
            className={classNames(
              "rounded-full px-4 py-1.5 text-sm border border-white/10 text-neutral-200",
              activeCategory === c ? `${accentBg} text-neutral-900 border-transparent` : "hover:bg-white/5"
            )}
          >{c}</button>
        ))}
      </div>
      <div className="relative w-full md:w-80">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search brand, title, or keyword…"
          className="w-full rounded-2xl border border-white/10 bg-neutral-900 px-4 py-2.5 pr-10 text-neutral-100 placeholder:text-neutral-500 outline-none focus:ring-2 focus:ring-amber-500/30"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500">⌕</span>
      </div>
    </div>
  );
}

function Catalog({ onOpen }) {
  const [activeCategory, setActiveCategory] = useState("All");
  const [query, setQuery] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cat = params.get("category");
    if (cat && cat !== activeCategory) setActiveCategory(cat);
    if (!cat && activeCategory !== "All") setActiveCategory("All");
  }, [location.search, activeCategory]);

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    const params = new URLSearchParams(location.search);
    if (cat && cat !== "All") params.set("category", cat);
    else params.delete("category");

    navigate({
      pathname: "/catalog",
      search: params.toString() ? `?${params.toString()}` : ""
    });
  };

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) =>
      (activeCategory === "All" ||
        p.category.toLowerCase() === activeCategory.toLowerCase()) &&
      (!q ||
        [p.title, p.brand, p.description]
          .some((s) => s.toLowerCase().includes(q)))
    );
  }, [activeCategory, query]);

  return (
    <section className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-white">
            {activeCategory !== "All" ? activeCategory : "Catalog"}
          </h2>
          <p className="mt-1 text-sm text-neutral-400">
            New pieces are added regularly. All items are authenticity-verified and quality-checked.
          </p>
        </div>

        <Filters
          activeCategory={activeCategory}
          setActiveCategory={handleCategoryChange}
          query={query}
          setQuery={setQuery}
        />

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((p) => (
            <article key={p.id} className={classNames(
              "group rounded-3xl bg-neutral-900 shadow-lg transition",
              subtleRing,
              "hover:shadow-amber-500/20 hover:ring-amber-500/30"
            )}>
              <div className="aspect-[4/3] overflow-hidden rounded-t-3xl bg-black">
                <img src={p.images[0]} alt={p.title} className="h-full w-full object-cover group-hover:scale-[1.02] transition opacity-95" />
              </div>
              <div className="p-4">
                <div className="text-xs uppercase tracking-wide text-neutral-400">
                  {p.brand} • {p.category}
                </div>
                <h3 className="mt-1 text-lg font-medium line-clamp-1 text-white">{p.title}</h3>
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-sm text-neutral-400">{p.condition}</span>
                  <span className={classNames("text-base font-semibold", accentText)}>
                    {currencyGBP(p.priceGBP)}
                  </span>
                </div>
                <button
                  onClick={() => onOpen(p)}
                  className="mt-4 w-full rounded-xl px-4 py-2 text-sm font-medium text-neutral-900 bg-white/90 hover:bg-white"
                >
                  View details
                </button>
              </div>
            </article>
          ))}
        </div>

        {list.length === 0 && (
          <div className="mt-16 rounded-3xl border border-white/10 bg-neutral-900 p-10 text-center text-neutral-400">
            No items match your filters yet. Try a different brand or category.
          </div>
        )}
      </div>
    </section>
  );
}

function ProductSheet({ product, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={classNames(
          "relative mx-auto my-8 w-full max-w-3xl rounded-3xl bg-neutral-950 shadow-2xl",
          subtleRing
        )}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 mr-2 rounded-full border border-white/10 bg-neutral-900/70 px-2 py-1 text-white hover:bg-white/10"
        >
          ×
        </button>

        <div className="max-h-[85vh] overflow-y-auto rounded-3xl">
          <div className="grid md:grid-cols-2">
            <div className="bg-black">
              <div className="aspect-[4/3] md:aspect-auto">
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="h-full w-full object-cover opacity-95"
                />
              </div>
            </div>

            <div className="p-6">
              <div className="text-xs uppercase tracking-wide text-neutral-400">
                {product.brand} • {product.category}
              </div>
              <h3 className="mt-1 text-2xl font-semibold text-white">{product.title}</h3>
              <span className="text-sm text-neutral-400">Product Code: {product.code}</span>

              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-neutral-400">{product.condition}</span>
                <span className={classNames("text-lg font-semibold", accentText)}>
                  {currencyGBP(product.priceGBP)}
                </span>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-neutral-300">
                {product.description}
              </p>

              <dl className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                {Object.entries(product.specs || {}).map(([k, v]) => (
                  <div
                    key={k}
                    className="flex flex-col rounded-xl border border-white/10 bg-neutral-900/80 px-3 py-2"
                  >
                    <dt className="text-neutral-400 capitalize">{k}</dt>
                    <dd className="mt-1 font-medium text-neutral-200">{v}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 rounded-2xl border border-white/10 bg-neutral-900/70 p-4">
                <h4 className="text-sm font-semibold text-white">How to purchase</h4>
                <p className="mt-1 text-sm text-neutral-300">
                  We don’t accept online payments on the site. To buy this item, please contact us via email or phone to
                  confirm availability and inspection options.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  <a
                    href={`mailto:${COMPANY_EMAIL}?subject=Purchase%20Inquiry:%20${encodeURIComponent(product.title)}`}
                    className="rounded-xl border border-white/10 px-3 py-1.5 text-neutral-200 hover:bg-white/5"
                  >
                    Email: {COMPANY_EMAIL}
                  </a>
                  <a
                    href={`tel:${COMPANY_PHONE.replace(/\s/g, "")}`}
                    className="rounded-xl border border-white/10 px-3 py-1.5 text-neutral-200 hover:bg-white/5"
                  >
                    Phone: {COMPANY_PHONE}
                  </a>
                  <a
                    href={`https://wa.me/${COMPANY_WHATSAPP.replace(/\D/g, "")}?text=${encodeURIComponent(
                      `Hi, I'm interested in ${product.title} (${product.id}).`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border border-white/10 px-3 py-1.5 text-neutral-200 hover:bg-white/5"
                  >
                    WhatsApp
                  </a>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={onClose}
                  className="rounded-xl px-4 py-2 text-sm text-neutral-300 hover:bg-white/5"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FeaturedSlider({ items = [], onOpen }) {
  const trackRef = useRef(null);
  const timerRef = useRef(null);

  const gapPx = 24; // matches `gap-6`

  const getStep = useCallback(() => {
    const el = trackRef.current;
    if (!el) return 0;
    const card = el.querySelector("[data-card]");
    // step = card width + gap; fallback to container width
    return card ? card.offsetWidth + gapPx : el.clientWidth;
  }, [gapPx]);

  const scrollNext = useCallback(
    (dir = 1) => {
      const el = trackRef.current;
      if (!el) return;

      const step = getStep();
      const max = el.scrollWidth - el.clientWidth;
      const epsilon = 2; // handle subpixel/snap rounding

      if (dir > 0) {
        if (el.scrollLeft < max - epsilon) {
          const next = Math.min(el.scrollLeft + step, max);
          el.scrollTo({ left: next, behavior: "smooth" });
        } else {
          el.scrollTo({ left: 0, behavior: "smooth" });
        }
        return;
      }

      // dir < 0
      if (el.scrollLeft > epsilon) {
        const prev = Math.max(el.scrollLeft - step, 0);
        el.scrollTo({ left: prev, behavior: "smooth" });
      } else {
        el.scrollTo({ left: max, behavior: "smooth" });
      }
    },
    [getStep]
  );

  const stopAuto = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  const startAuto = useCallback(() => {
    stopAuto();
    timerRef.current = setInterval(() => scrollNext(1), 2000);
  }, [stopAuto, scrollNext]);

  useEffect(() => {
    startAuto();

    // restart auto-play if layout changes (e.g., resize / cards wrap)
    const ro = new ResizeObserver(() => {
      // simple restart debounce
      startAuto();
    });
    if (trackRef.current) ro.observe(trackRef.current);

    return () => {
      stopAuto();
      ro.disconnect();
    };
  }, [startAuto, stopAuto]);

  return (
    <div
      className="relative"
      onMouseEnter={stopAuto}
      onMouseLeave={startAuto}
      onPointerDown={stopAuto}
      onPointerUp={startAuto}
    >
      {/* Track */}
      <div
        ref={trackRef}
        className="flex gap-6 overflow-x-auto no-scrollbar snap-x snap-mandatory scroll-smooth py-1"
      >
        {items.map((p) => (
          <article
            key={p.id}
            data-card
            className={classNames(
              "snap-start shrink-0 w-full md:w-[calc((100%-24px)/2)] lg:w-[calc((100%-48px)/3)]",
              "group rounded-3xl bg-neutral-900 shadow transition",
              subtleRing,
              "hover:ring-amber-500/30 hover:shadow-amber-500/20"
            )}
          >
            <div className="aspect-[4/3] overflow-hidden rounded-t-3xl bg-black">
              <img
                src={p.images[0]}
                alt={p.title}
                className="h-full w-full object-cover group-hover:scale-[1.02] transition opacity-95"
              />
            </div>
            <div className="p-4">
              <div className="text-xs uppercase tracking-wide text-neutral-400">
                {p.brand} • {p.category}
              </div>
              <h3 className="mt-1 text-lg font-medium line-clamp-1 text-white">{p.title}</h3>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-neutral-400">{p.condition}</span>
                <span className={classNames("text-base font-semibold", accentText)}>
                  {currencyGBP(p.priceGBP)}
                </span>
              </div>
              <button
                onClick={() => onOpen(p)}
                className="mt-4 w-full rounded-xl bg-white/90 px-4 py-2 text-sm font-medium text-neutral-900 hover:bg-white"
              >
                View details
              </button>
            </div>
          </article>

        ))}
      </div>

      {/* Controls */}
      <div className="mt-6 flex justify-center gap-4">
        <button
          aria-label="Previous"
          onClick={() => scrollNext(-1)}
          className="rounded-full border border-white/10 bg-neutral-900/80 px-4 py-2 text-white hover:bg-white/10"
        >
          ‹
        </button>
        <button
          aria-label="Next"
          onClick={() => scrollNext(1)}
          className="rounded-full border border-white/10 bg-neutral-900/80 px-4 py-2 text-white hover:bg-white/10"
        >
          ›
        </button>
      </div>

    </div>
  );
}

function About() {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <h2 className="text-2xl font-semibold text-white">About {COMPANY_NAME} Online Limited</h2>
        <p className="mt-4 leading-relaxed text-neutral-300">
          We’re a trusted reseller of authentic luxury accessories. We buy and sell pre‑owned designer bags and watches -
          connecting fashion enthusiasts with genuine, high‑quality pieces from the world’s leading brands. Our mission is to make luxury more accessible, sustainable, and smart for everyone.
        </p>
        <p className="mt-4 leading-relaxed text-neutral-300">
          We don’t manufacture products - we carefully source and verify them. Every item in our collection is authentic,
          quality‑checked, and ready for a new owner. Whether you’re selling a piece from your wardrobe or searching for your next luxury find, our platform ensures a smooth and secure experience.
        </p>
        <div className="mt-6 rounded-2xl border border-white/10 bg-neutral-900/60 p-4 text-sm">
          <h3 className="font-semibold text-white">Commitments</h3>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-neutral-300">
            <li>Authenticity verification for every item</li>
            <li>Transparent condition grading</li>
            <li>Private, secure, appointment‑based purchase process</li>
            <li>Fair pricing and great value</li>
            <li>Giving timeless fashion a second life</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  return (
    <section className="py-12">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <h2 className="text-2xl font-semibold text-white">Contact</h2>
        <p className="mt-3 text-neutral-300">Reach out to inquire about availability, schedule an inspection, or discuss consignment/selling.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <a href={`mailto:${COMPANY_EMAIL}`} className={classNames("rounded-2xl p-4 hover:bg-white/5 text-neutral-200", subtleRing)}>
            <div className="text-sm font-semibold text-white">Email</div>
            <div className="text-neutral-300">{COMPANY_EMAIL}</div>
          </a>
          <a href={`tel:${COMPANY_PHONE.replace(/\s/g, "")}`} className={classNames("rounded-2xl p-4 hover:bg-white/5 text-neutral-200", subtleRing)}>
            <div className="text-sm font-semibold text-white">Phone</div>
            <div className="text-neutral-300">{COMPANY_PHONE}</div>
          </a>
          <a target="_blank" rel="noreferrer" href={`https://wa.me/${COMPANY_WHATSAPP.replace(/\D/g, "")}`} className={classNames("rounded-2xl p-4 hover:bg-white/5 text-neutral-200", subtleRing)}>
            <div className="text-sm font-semibold text-white">WhatsApp</div>
            <div className="text-neutral-300">{COMPANY_WHATSAPP}</div>
          </a>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://www.google.com/maps?q=${encodeURIComponent(COMPANY_ADDRESS)}`}
            className={classNames("rounded-2xl p-4 hover:bg-white/5 text-neutral-200", subtleRing)}
          >
            <div className="text-sm font-semibold text-white">Address</div>
            <div className="text-neutral-300">{COMPANY_ADDRESS}</div>
          </a>
          <div className={classNames("rounded-2xl p-4 text-neutral-200", subtleRing)}>
            <div className="text-sm font-semibold text-white">Hours</div>
            <div className="text-neutral-300">By appointment</div>
          </div>
        </div>
        <div className="mt-8 rounded-2xl border border-white/10 bg-neutral-900/60 p-4 text-sm">
          <h3 className="font-semibold text-white">Buyer advisory</h3>
          <p className="mt-1 text-neutral-300">
            We do not accept online payments on this website. All purchases are coordinated directly via email/phone so we can verify identity, confirm item condition, and arrange a safe exchange or delivery.
          </p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-neutral-900">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div><Link to="/" className="flex items-center gap-2 hover:opacity-90">
              <img
                src="/logo.png"
                alt={COMPANY_NAME}
                className="h-16 w-auto mb-2 rounded-md"
              />
            </Link>
            </div>
            <div className="text-sm text-neutral-400">
              Authentic Pre-Owned Luxury • Nottingham & Beyond
            </div>
          </div>
          <div className="flex gap-4 text-sm text-neutral-300">
            <Link to="/about" className="hover:text-white">About</Link>
            <Link to="/catalog" className="hover:text-white">Catalog</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
        <div className="mt-6 text-xs text-neutral-500">
          © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.{" "}
          Developed by{" "}
          <a
            href="https://techniqlysolutions.com"
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-neutral-300 hover:text-white transition"
          >
            Techniqly Solutions
          </a>.
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const [openProduct, setOpenProduct] = useState(null);

  return (
    <Router>
      <div className="min-h-screen bg-neutral-950 text-neutral-100">
        <Header />
        <Routes>
          <Route path="/" element={<Home onOpen={setOpenProduct} />} />
          <Route path="/catalog" element={<Catalog onOpen={setOpenProduct} />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        <Footer />
        <ProductSheet product={openProduct} onClose={() => setOpenProduct(null)} />
      </div>
    </Router>
  );
}

function Home({ onOpen }) {
  const navigate = useNavigate();

  return (
    <>
      <Hero onShop={() => navigate("/catalog")} />
      <main>
        <section className="py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { title: "Bags", img: "/bag.jpg" },
                { title: "Watches", img: "/watch.jpg" },
              ].map((c) => (
                <Link
                  key={c.title}
                  to={`/catalog?category=${encodeURIComponent(c.title)}`}
                  className={classNames(
                    "group overflow-hidden rounded-3xl bg-neutral-900 shadow",
                    subtleRing,
                    "hover:ring-amber-500/30 hover:shadow-amber-500/20"
                  )}
                >
                  <div className="aspect-[4/3] overflow-hidden bg-black">
                    <img src={c.img} alt={c.title} className="h-full w-full object-cover group-hover:scale-[1.02] transition opacity-95" />
                  </div>
                  <div className="p-4 text-left">
                    <div className="text-sm uppercase tracking-wide text-neutral-400">Shop</div>
                    <div className="text-lg font-medium text-white">{c.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="pb-12">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className={classNames("rounded-3xl bg-neutral-900 p-6", subtleRing)}>
              <h3 className="text-xl font-semibold text-white">Why buy with us</h3>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm text-neutral-300">
                <li className="rounded-2xl bg-neutral-950/60 p-3 border border-white/10">✔ Authenticity checks for every item</li>
                <li className="rounded-2xl bg-neutral-950/60 p-3 border border-white/10">✔ Transparent condition grading</li>
                <li className="rounded-2xl bg-neutral-950/60 p-3 border border-white/10">✔ Private, secure purchase process</li>
                <li className="rounded-2xl bg-neutral-950/60 p-3 border border-white/10">✔ Sustainable & great value</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-white">Featured items</h2>
                <p className="text-sm text-neutral-400">Hand-picked highlights from our current selection.</p>
              </div>
              <Link to="/catalog" className="text-sm text-neutral-300 hover:text-white">View all →</Link>
            </div>
            <FeaturedSlider items={PRODUCTS} onOpen={onOpen} />
          </div>
        </section>
      </main>
    </>
  );
}
